import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth, useAppSelector } from '@verantos-dev/core-annotation-tool';
import type { Annotation, Concept } from '@verantos-dev/core-annotation-tool';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';

import { useGetSupervisorWorkspaceQuery } from '../../api/supervisorApiSlice';
import { useSupervisorMode } from '../../context/SupervisorModeContext';
import { selectSelectedNoteId } from '../../state/selector';
import type { Encounter, Note } from '../../types/response';

export interface UseSupervisorContentResult {
  encounterData: Encounter | undefined;
  isEncounterDataLoading: boolean;
  selectedNote: Note | null;
  taskStatus: string | null;
  concepts: Concept[] | null;
  annotations: Annotation[] | null;
}

export const useSupervisorContent = (): UseSupervisorContentResult => {
  const { encounterId } = useParams();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();

  const selectedNoteId = useAppSelector(selectSelectedNoteId);
  const batchId = useAppSelector(selectBatchId);
  const {
    data: supervisorWorkspaceData,
    isLoading: isSupervisorWorkspaceLoading,
    isFetching: isSupervisorWorkspaceFetching,
  } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );

  const selectedNote = useMemo(() => {
    if (!supervisorWorkspaceData?.data?.notes || !selectedNoteId) return null;
    return supervisorWorkspaceData.data.notes.find((n) => n.noteId === selectedNoteId) ?? null;
  }, [supervisorWorkspaceData, selectedNoteId]);

  const annotations = useMemo((): Annotation[] | null => {
    const annotationsReview = supervisorWorkspaceData?.data?.annotationsReview;
    if (!annotationsReview) return null;
    const result: Annotation[] = [];
    for (const review of annotationsReview) {
      result.push(...review.matchingAnnotation);
      for (const conflict of review.conflicts) {
        if (conflict.resolved.annotations?.length) {
          result.push(...conflict.resolved.annotations);
        }
      }
    }
    return result;
  }, [supervisorWorkspaceData]);

  return {
    encounterData: supervisorWorkspaceData?.data?.encounter,
    isEncounterDataLoading: isSupervisorWorkspaceLoading || isSupervisorWorkspaceFetching,
    selectedNote,
    taskStatus: supervisorWorkspaceData?.data?.task.status ?? null,
    concepts: supervisorWorkspaceData?.data?.concepts ?? null,
    annotations,
  };
};
